import { SortEnum } from "@finway-group/shared/lib/consts"
import { Expense, FilterBaseInterface, QueryParamsObject, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { VendorTableColumnKeys } from "Features/pages/vendors/vendorTableConsts"
import { queryClient } from "Shared/config/reactQueryConfig"
import { UserService, VendorService } from "Shared/services"
import { createInitialReactQueryTableData } from "Shared/store/reducers/tableReducer"
import { convertFilterObjectIntoQueryFormat } from "Shared/utils/filter.utils"
import { convertSearchValueToQueryObject, isSet } from "Shared/utils/helper.utils"

import { UserCreditor } from "../../utils/vendor.utils"
import { CacheEnum } from "../consts"
import { getSortQueryObject } from "../query.utils"
import { QueryParams, UserCreditorDropDown, VENDOR_INFINITE_SCROLL_PAGE_LIMIT, VendorDropdown } from "./consts"
import { ReactQueryTableData } from "./types"

export const useExpenseVendor = (expense: Expense) => useVendor(expense?.vendor?._id, expense.vendorType === VendorTypeEnum.VENDOR)
export const useExpenseCreditor = (expense: Expense) => useVendor(expense.getCreditor()?._id, expense.vendorType === VendorTypeEnum.VENDOR)

export const useVendor = (vendorId: string | undefined, isEnabled: boolean = true) =>
    useQuery({
        queryKey: [CacheEnum.VENDORS, vendorId],
        queryFn: () => VendorService.fetchOne(vendorId),
        select: (vendor: Vendor) => new Vendor(vendor),
        enabled: Boolean(vendorId) && isEnabled,
    })

export const useVendorList = (queryParams: QueryParams, filterData: FilterBaseInterface | undefined = {}) => {
    const loggedInEmployee = UserService.getLoggedInEmployeeProfile()
    const shouldShowGrossAmount = loggedInEmployee?.settings?.showGrossAmount

    const { page, limit, sortOption, sortField } = queryParams
    const filter = { deleted: { is: false }, ...convertFilterObjectIntoQueryFormat(filterData, shouldShowGrossAmount) }
    const search = isSet(queryParams.search)
        ? convertSearchValueToQueryObject(queryParams?.search!, [VendorTableColumnKeys.NAME, VendorTableColumnKeys.CREDITOR_NUMBER])
        : undefined
    const queryParamsObject = { page, limit, ...getSortQueryObject({ field: sortField, order: sortOption }), ...filter, ...search }

    const queryResult = useQuery<ReactQueryTableData<Vendor>>({
        queryKey: [CacheEnum.VENDORS_LIST, queryParams, queryParamsObject],
        queryFn: async ({ signal }) => {
            const data = await VendorService.fetchAllVendors(queryParamsObject, signal)
            return data
        },
        placeholderData: createInitialReactQueryTableData(),
    })

    return queryResult
}

export const useManyVendors = (vendorIds: Array<string>, { excludeDeleted = true }: { excludeDeleted?: boolean } = {}) => {
    const uniqueVendorIds = [...new Set(vendorIds)]
    // Reason: By changing the order of the IDs that are cached, we will not hit the same cache. That is, order change will result in creating a new cache entry.
    // To prevent that and re-used the cached data if only the order changes, we objectify the unique keys
    const objectifiedUniqueVendorIds = Object.fromEntries(uniqueVendorIds.map((key) => [key, ""]))

    const queryResult = useQuery({
        queryKey: [CacheEnum.VENDORS_MANY, objectifiedUniqueVendorIds],
        queryFn: async ({ signal }) => {
            const vendorsInCache = []
            const vendorIdsToFetch = []

            for (const id of uniqueVendorIds) {
                const vendorInCache = getVendorFromCache(id)
                if (vendorInCache) {
                    vendorsInCache.push(vendorInCache)
                } else {
                    vendorIdsToFetch.push(id)
                }
            }

            if (!vendorIdsToFetch.length) {
                return vendorsInCache
            }

            const deletedFilter = excludeDeleted ? { deleted: { is: false } } : undefined
            const filter = vendorIdsToFetch.length
                ? {
                      _id: { in: vendorIdsToFetch },
                      ...deletedFilter,
                  }
                : deletedFilter

            const queryParams: QueryParamsObject = {
                page: 1,
                limit: Math.min(1000, vendorIdsToFetch.length),
                ...filter,
            }

            const data = (await VendorService.fetchAllVendors(queryParams, signal)).docs

            return [...vendorsInCache, ...data]
        },
        enabled: vendorIds?.length > 0,
    })

    return queryResult
}

const getVendorFromCache = (vendorId: string) => {
    const queryKey = [CacheEnum.VENDORS, vendorId]
    const state = queryClient.getQueryState(queryKey)

    return state?.data && state?.isInvalidated === false ? new Vendor(state.data) : null
}

export const useVendorDropdown = (searchString: string, isEnabled: boolean = true) =>
    useInfiniteQuery({
        queryKey: [CacheEnum.VENDORS_DROPDOWN, searchString],
        queryFn: async ({ pageParam = 1 }) => {
            const search = isSet(searchString) ? convertSearchValueToQueryObject(searchString!, [VendorTableColumnKeys.NAME, VendorTableColumnKeys.CREDITOR_NUMBER]) : undefined
            const queryParameters: QueryParamsObject = {
                page: pageParam,
                limit: VENDOR_INFINITE_SCROLL_PAGE_LIMIT,
                ...getSortQueryObject({ field: "name", order: SortEnum.ASC }),
                ...search,
            }
            const data = await VendorService.fetchAllVendors(queryParameters)
            return data
        },
        enabled: isEnabled,
        select: (data) => ({ ...data, pages: data.pages.map((page) => page.docs.map((doc) => mapVendorCacheToDropdownOption(doc))) }),
        getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.nextPage : undefined),
    })

export const fetchVendor = (vendorId: string | undefined) => {
    if (!vendorId) {
        return undefined
    }
    return queryClient.fetchQuery({
        queryKey: [CacheEnum.VENDORS, vendorId],
        queryFn: () => VendorService.fetchOne(vendorId),
    })
}

const mapVendorCacheToDropdownOption = (vendor: Vendor) => mapVendorToSelectOption(vendor)

/**
 * Invalidates vendors lists used for the vendor table (VENDORS_LIST), subscription table, card transaction table, etc. (VENDORS_MANY), and drop-downs (VENDORS_DROPDOWN)
 *
 */
export const invalidateVendorLists = () => {
    queryClient.invalidateQueries({
        queryKey: [CacheEnum.VENDORS_LIST],
    })
    queryClient.invalidateQueries({
        queryKey: [CacheEnum.VENDORS_MANY],
    })
    queryClient.invalidateQueries({
        queryKey: [CacheEnum.VENDORS_DROPDOWN],
    })
}

/**
 * Invalidates everything cached along the path `CacheEnum.VENDORS`. This will invalidate the selected/currently open vendor that's cached at `CacheEnum.VENDORS/<ID>` as well.
 *
 */
export const invalidateVendors = () => {
    queryClient.invalidateQueries({
        queryKey: [CacheEnum.VENDORS],
    })
}

export const invalidateVendor = (vendorId: string | undefined) => {
    if (!vendorId) {
        return
    }

    queryClient.invalidateQueries({
        queryKey: [CacheEnum.VENDORS, vendorId],
    })
}

export const mapVendorToSelectOption = (vendor: Vendor): VendorDropdown => {
    const { name, isApproved, creditorNumber, img, deleted, dateDeleted, id } = vendor
    return {
        id,
        name,
        isApproved,
        creditorNumber,
        img,
        deleted,
        dateDeleted,
        type: VendorTypeEnum.VENDOR,
    }
}
export const mapUserCreditorToSelectOption = (userCreditor: UserCreditor): UserCreditorDropDown => {
    const { name, creditorNumber, img, deleted, dateDeleted, id } = userCreditor
    return {
        id,
        name,
        creditorNumber,
        img,
        deleted,
        dateDeleted,
        type: VendorTypeEnum.USER,
    }
}
